import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FileSignature, LayoutDashboard, Building2, DoorOpen, Users, Receipt, BadgeCheck, Zap, FolderOpen,
  MessageSquare, BellRing, Wrench, Settings, LogOut, Menu, X, Sun, Moon, ChevronRight,
  LifeBuoy,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useApi } from '@/lib/useApi';
import { Avatar, Badge, cn } from './ui';
import { FeedbackButton } from './Feedback';
import type { DashboardStats } from '@shared/types';

const NAV = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/app/properties', label: 'Properties', icon: Building2 },
  { to: '/app/rooms', label: 'Rooms', icon: DoorOpen },
  { to: '/app/tenants', label: 'Tenants', icon: Users },
  { to: '/app/payments', label: 'Payments', icon: BadgeCheck, badge: 'pending' as const },
  { to: '/app/charges', label: 'Rent ledger', icon: Receipt },
  { to: '/app/reminders', label: 'Reminders', icon: BellRing, badge: 'reminders' as const },
  { to: '/app/bills', label: 'Bills', icon: Zap },
  { to: '/app/documents', label: 'Documents', icon: FolderOpen },
  { to: '/app/agreements', label: 'Agreements', icon: FileSignature },
  { to: '/app/messages', label: 'Messages', icon: MessageSquare, badge: 'messages' as const },
  { to: '/app/maintenance', label: 'Maintenance', icon: Wrench, badge: 'maintenance' as const },
  { to: '/app/settings', label: 'Settings', icon: Settings },
  { to: '/app/help', label: 'Help', icon: LifeBuoy },
];

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      // Exact match only, so a junk value lands on the default rather than
      // being set as the attribute. Light is the default; see src/main.tsx,
      // which has to agree with this or the first paint flashes.
      return localStorage.getItem('provia.theme') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('provia.theme', theme); } catch { /* storage blocked */ }
  }, [theme]);

  return { theme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) };
}

export function Shell() {
  const { staff, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggle } = useTheme();

  // The sidebar counters double as the app's alert system.
  const { data: stats } = useApi<DashboardStats>('/dashboard');

  // Close the drawer whenever navigation happens.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const badgeFor = (key?: 'pending' | 'reminders' | 'maintenance' | 'messages') => {
    if (!key || !stats) return null;
    const value = key === 'pending' ? stats.pending_verification_count
      : key === 'messages' ? stats.messages_awaiting_reply
      : key === 'reminders' ? stats.reminder_queue_count
      : stats.open_maintenance_count;
    if (!value) return null;
    return (
      <span className={cn(
        'ml-auto text-xs font-bold tnum px-1.5 py-0.5 rounded-md',
        key === 'pending' ? 'bg-warn/20 text-warn' : 'bg-raised text-muted',
      )}>
        {value}
      </span>
    );
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 h-16 shrink-0">
        <Logo />
        <div className="min-w-0">
          <p className="font-extrabold tracking-tight leading-none">PROVIA</p>
          <p className="text-[11px] text-faint truncate mt-0.5">{staff?.org.name}</p>
        </div>
        <button
          className="ml-auto lg:hidden p-1.5 text-faint hover:text-ink"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => cn(
              // min-w-0 lets the label truncate instead of forcing the row
              // wider than the sidebar, which is what made the highlight
              // spill past the edge when a label and a badge were both long.
              'flex items-center gap-3 min-w-0 w-full px-3 py-2 rounded-xl',
              'text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand/12 text-brand'
                : 'text-muted hover:text-ink hover:bg-raised',
            )}
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate min-w-0 flex-1">{item.label}</span>
            {badgeFor(item.badge)}
          </NavLink>
        ))}
      </nav>

      {/*
        Two rows, not one. The first version put the profile, the theme toggle
        and a labelled Sign out on a single line, and hid the label with
        `sm:` — a *viewport* breakpoint, which is always true on desktop and
        says nothing about the width of a fixed 256px sidebar. The label
        therefore always rendered and pushed the button past the edge.
      */}
      <div className="p-3 border-t border-line shrink-0 space-y-1">
        <div className="flex items-center gap-2.5 min-w-0 px-2 py-1.5">
          <Avatar name={staff?.full_name ?? '?'} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{staff?.full_name}</p>
            <p className="text-[11px] text-faint capitalize truncate">{staff?.role}</p>
          </div>
          <button
            onClick={toggle}
            className="shrink-0 p-1.5 rounded-lg text-faint hover:text-ink hover:bg-raised transition-colors"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>

        {/*
          Next to Sign out on purpose: it is the one control in the same place
          on every screen, and the moment somebody wants to report a problem is
          usually the moment they have given up on what they were doing.
        */}
        <FeedbackButton />

        <button
          onClick={() => { signOut(); navigate('/'); }}
          className="flex items-center gap-3 w-full min-w-0 px-3 py-2 rounded-xl
                     text-sm font-medium text-muted transition-colors
                     hover:text-danger hover:bg-danger/10"
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          <span className="truncate">Sign out</span>
        </button>

        {/*
          Whose software this is, once, at the bottom of the sidebar.

          PROVIA is the product; OPSERION is the company that builds and runs
          it, and is who the contract, the invoice and any complaint are with.
          Somebody should be able to find that out without reading the terms —
          not least when they are deciding whether an email claiming to be from
          us is genuine.
        */}
        <p className="px-3 pt-1 text-[10px] leading-relaxed text-faint">
          PROVIA is operated by OPSERION.{' '}
          <Link to="/app/help" className="hover:text-muted underline decoration-line">
            Contact us
          </Link>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-line bg-surface z-30">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 bg-surface border-r border-line z-50 animate-fade-up">
            {sidebar}
          </aside>
        </>
      )}

      <div className="lg:pl-64">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 h-14 px-4
                           bg-surface/90 backdrop-blur border-b border-line">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 -ml-1.5 text-muted hover:text-ink"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Logo className="h-6 w-6" />
          <span className="font-bold tracking-tight">PROVIA</span>
          {!!stats?.pending_verification_count && (
            <Badge tone="warn" className="ml-auto">
              {stats.pending_verification_count} to verify
            </Badge>
          )}
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-[1600px] mx-auto">
          <SubscriptionBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Says when the trial is running out, or that the account is blocked.
 *
 * On every screen rather than only in Settings: a manager who cannot save a
 * change needs to know why on the screen where it failed, not after hunting
 * through settings for an explanation.
 */
function SubscriptionBanner() {
  const { data } = useApi<{
    status: string; active: boolean; is_demo: boolean; trial_days_left: number | null;
  }>('/subscription');

  if (!data || data.is_demo || data.status === 'active') return null;

  const blocked = !data.active;
  const days = data.trial_days_left ?? 0;

  // Quiet early in a trial; it only earns space near the end.
  if (!blocked && days > 3) return null;

  return (
    <div
      className={cn(
        'mb-6 rounded-2xl border p-4 flex flex-wrap items-start gap-x-4 gap-y-2',
        blocked ? 'border-danger/30 bg-danger/8' : 'border-warn/30 bg-warn/8',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink text-sm">
          {blocked
            ? 'This organisation is blocked'
            : `${days} day${days === 1 ? '' : 's'} left on your trial`}
        </p>
        <p className="text-sm text-muted mt-1 leading-relaxed">
          {blocked
            ? 'Nothing new can be saved until payment is received. Nothing has been deleted — everything you entered is still here and can be read and exported.'
            : 'After that, nothing new can be saved until payment is received. Your data stays either way.'}
        </p>
      </div>
      <NavLink
        to="/app/settings"
        className={cn(
          'shrink-0 rounded-lg px-3.5 py-2 text-sm font-medium transition-opacity hover:opacity-90',
          blocked ? 'bg-danger text-white' : 'bg-warn text-white',
        )}
      >
        {blocked ? 'Unblock' : 'Choose a plan'}
      </NavLink>
    </div>
  );
}

export function Logo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={cn('h-8 w-8 shrink-0', className)} aria-hidden>
      <rect width="64" height="64" rx="14" fill="rgb(var(--raised))" />
      <path
        d="M14 46V22.5L32 12l18 10.5V46"
        fill="none" stroke="rgb(var(--brand))" strokeWidth="5"
        strokeLinejoin="round" strokeLinecap="round"
      />
      <path
        d="M26 46V33h12v13"
        fill="none" stroke="rgb(var(--accent))" strokeWidth="5"
        strokeLinejoin="round" strokeLinecap="round"
      />
    </svg>
  );
}

/** Back-link used above detail page titles. */
export function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="flex items-center gap-1.5 text-sm text-faint flex-wrap">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5" />}
          {item.to ? (
            <NavLink to={item.to} className="hover:text-ink transition-colors">{item.label}</NavLink>
          ) : (
            <span className="text-muted">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
